import { Trade } from '@/models/registry';
import type { Trade as TradeType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

export class TradeStore implements BaseStore<Trade, TradeType> {
  store: Map<string, Trade> = new Map();

  storeIndexedByShortId: Map<string, Trade> = new Map();

  storeIndexedByForeignKey: Map<string, Trade[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setTradeField = <K extends keyof TradeType>(
    trade: Trade,
    key: K,
    value: TradeType[K],
  ) => {
    (trade as Pick<Trade, K>)[key] = value as Trade[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TradeType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.trades.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.trades.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.trades.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.trades.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({ delta }: { delta: DeltaPacketRecord<TradeType> }) {
    const existingTrade = this.getById(delta.record_id);
    if (!existingTrade) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof TradeType,
      TradeType[keyof TradeType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingTrade[key] === newValue) {
        continue;
      }

      this.setTradeField(existingTrade, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingTrade);
  }

  get getTrades(): Trade[] {
    return Array.from(this.store.values());
  }

  getById(id: string): Trade | undefined {
    return this.store.get(id);
  }

  getByShortId(shortId: string): Trade | undefined {
    return this.storeIndexedByShortId.get(shortId);
  }

  add(input: TradeType) {
    const trade = new Trade(input);
    this.store.set(trade.id, trade);
    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(trade.deal_id) || [];
    arr.push(trade);
    this.storeIndexedByForeignKey.set(trade.deal_id, arr);

    // Index by short id for O(1) lookups
    this.storeIndexedByShortId.set(trade.short_id, trade);
  }

  remove(id: string) {
    const tradeIn = this.store.get(id);
    if (!tradeIn) return;

    this.store.delete(id);
    this.storeIndexedByForeignKey.delete(tradeIn.deal_id);
  }

  update({ id, trade }: { id: Trade['id']; trade: Partial<TradeType> }) {
    let tradeInstance = this.store.get(id);
    if (!tradeInstance) return;

    tradeInstance = new Trade({
      ...tradeInstance,
      ...trade,
    });

    console.log('tradeStore update tradeInstance', tradeInstance);

    this.replace({ id, trade: tradeInstance });
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<TradeType>[];
    hydrateIndexedDB?: boolean;
  }): void {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TradeType>;
    hydrateIndexedDB?: boolean;
  }): void {
    if (hydrateIndexedDB) {
      db.trades.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  upsert(trade: TradeType) {
    const existingTrade = this.getById(trade.id);
    if (existingTrade) {
      this.replace({ id: trade.id, trade });
    } else {
      this.add(trade);
    }
  }

  replace({ id, trade }: { id: string; trade: TradeType }) {
    const existingTrade = this.getById(id);
    if (!existingTrade) return;

    existingTrade.status = trade.status;
    existingTrade.title_present = trade.title_present;
    existingTrade.payoff_amount = trade.payoff_amount;
    existingTrade.payoff_expiration = trade.payoff_expiration;
    existingTrade.appraised_value = trade.appraised_value;
    existingTrade.ever_location = trade.ever_location;
    existingTrade.created_by_id = trade.created_by_id;
    existingTrade.deal_id = trade.deal_id;
    existingTrade.full_name = trade.full_name;
    existingTrade.vin = trade.vin;
    existingTrade.make = trade.make;
    existingTrade.model = trade.model;
    existingTrade.trim = trade.trim;
    existingTrade.year = trade.year;
    existingTrade.mileage = trade.mileage;
    existingTrade.condition = trade.condition;
    existingTrade.paid_off = trade.paid_off;
    existingTrade.lienholder = trade.lienholder;
    existingTrade.state = trade.state;
    existingTrade.postal_code = trade.postal_code;
    existingTrade.number_of_keys = trade.number_of_keys;
    existingTrade.asking_price = trade.asking_price;
    existingTrade.exterior_color = trade.exterior_color;
    existingTrade.interior_color = trade.interior_color;
    existingTrade.offer = trade.offer;
    existingTrade.resell_type = trade.resell_type;
    existingTrade.status = trade.status;
    existingTrade.current_location = trade.current_location;
    existingTrade.auctions = trade.auctions;

    this.save(existingTrade);
  }

  save(trade: Trade) {
    // DEV NOTE: we want the getter to recompute whenever any of these values change
    // but mobx's observable.map only recomputes when an addition or deletion occurs
    // Thus, we need to delete value right before setting it again

    this.store.delete(trade.id);
    this.store.set(trade.id, trade);

    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(trade.deal_id) || [];
    const index = arr.findIndex((d) => d.id === trade.id);
    if (index !== -1) {
      arr[index] = trade;
    }

    this.storeIndexedByForeignKey.delete(trade.deal_id);
    this.storeIndexedByForeignKey.set(trade.deal_id, arr);

    // Index by short id for O(1) lookups
    this.storeIndexedByShortId.delete(trade.short_id);
    this.storeIndexedByShortId.set(trade.short_id, trade);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'trades',
      entity: 'trades',
      modelStore: tradeStore,
    });
  }
}

export const tradeStore = new TradeStore();
